import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface ArcPlaybackContextValue {
  isPlaying: boolean;
  playbackSpeed: number;
  elapsedMs: number;
  elapsedMsRef: React.MutableRefObject<number>;
  totalDurationMs: number;
  strokeDurationMs: number;
  strokeCount: number;
  currentStrokeIndex: number;
  strokeProgress: number;
  totalProgress: number;
  togglePlay: () => void;
  setPlaying: (value: boolean) => void;
  setPlaybackSpeed: (speed: number) => void;
  seekToProgress: (progress: number) => void;
  seekToStroke: (index: number) => void;
  reset: () => void;
}

const ArcPlaybackContext = createContext<ArcPlaybackContextValue | undefined>(undefined);

interface ArcPlaybackProviderProps {
  strokeCount: number;
  strokeDurationMs?: number;
  initialPlaying?: boolean;
  loop?: boolean;
  children: React.ReactNode;
}

export const ArcPlaybackProvider: React.FC<ArcPlaybackProviderProps> = ({
  strokeCount,
  strokeDurationMs = 2200,
  initialPlaying = true,
  loop = true,
  children,
}) => {
  const [isPlaying, setIsPlaying] = useState(initialPlaying);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [currentStrokeIndex, setCurrentStrokeIndex] = useState(0);

  const isPlayingRef = useRef(isPlaying);
  const playbackSpeedRef = useRef(playbackSpeed);
  const elapsedMsRef = useRef(0);
  const committedElapsedMsRef = useRef(0);
  const currentStrokeIndexRef = useRef(0);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
  }, [playbackSpeed]);

  const totalDurationMs = useMemo(
    () => Math.max(1, strokeCount) * strokeDurationMs,
    [strokeCount, strokeDurationMs]
  );

  const computeStrokeIndex = useCallback(
    (elapsed: number) => {
      if (strokeCount === 0) {
        return 0;
      }
      const driveCompletionOffset = strokeDurationMs * 0.45;
      const index = Math.floor((elapsed + driveCompletionOffset) / strokeDurationMs);
      return Math.min(strokeCount - 1, Math.max(0, index));
    },
    [strokeCount, strokeDurationMs]
  );

  const commitPlaybackTime = useCallback(
    (nextElapsed: number, forceDisplayCommit: boolean = false) => {
      elapsedMsRef.current = nextElapsed;

      const nextStrokeIndex = computeStrokeIndex(nextElapsed);
      if (nextStrokeIndex !== currentStrokeIndexRef.current) {
        currentStrokeIndexRef.current = nextStrokeIndex;
        setCurrentStrokeIndex(nextStrokeIndex);
      }

      const previousBucket = Math.floor(committedElapsedMsRef.current / 100);
      const nextBucket = Math.floor(nextElapsed / 100);
      if (forceDisplayCommit || previousBucket !== nextBucket) {
        committedElapsedMsRef.current = nextElapsed;
        setElapsedMs(nextElapsed);
      }
    },
    [computeStrokeIndex]
  );

  useEffect(() => {
    let animationFrameId: number | null = null;
    let lastTimestamp: number | null = null;

    const step = (timestamp: number) => {
      if (lastTimestamp != null) {
        const delta = (timestamp - lastTimestamp) * playbackSpeedRef.current;
        let next = elapsedMsRef.current + delta;
        if (loop) {
          next = next % totalDurationMs;
        } else {
          next = Math.min(next, totalDurationMs);
        }
        commitPlaybackTime(next);
      }

      lastTimestamp = timestamp;
      animationFrameId = requestAnimationFrame(step);
    };

    if (isPlaying) {
      animationFrameId = requestAnimationFrame(step);
    } else {
      lastTimestamp = null;
    }

    return () => {
      if (animationFrameId != null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [commitPlaybackTime, isPlaying, loop, totalDurationMs]);

  useEffect(() => {
    // Clamp elapsed time when stroke count or duration changes
    let next = elapsedMsRef.current;
    if (loop) {
      next = next % totalDurationMs;
    } else {
      next = Math.min(next, totalDurationMs);
    }
    commitPlaybackTime(next, true);
  }, [commitPlaybackTime, loop, totalDurationMs]);

  const strokeProgress = useMemo(() => {
    if (strokeDurationMs === 0) return 0;
    return (elapsedMs % strokeDurationMs) / strokeDurationMs;
  }, [elapsedMs, strokeDurationMs]);

  const totalProgress = useMemo(() => {
    if (totalDurationMs === 0) return 0;
    return elapsedMs / totalDurationMs;
  }, [elapsedMs, totalDurationMs]);

  const togglePlay = useCallback(() => {
    setIsPlaying((previous) => !previous);
  }, []);

  const setPlaying = useCallback((value: boolean) => {
    setIsPlaying(value);
  }, []);

  const seekToProgress = useCallback(
    (progress: number) => {
      const clamped = Math.min(1, Math.max(0, progress));
      commitPlaybackTime(clamped * totalDurationMs, true);
    },
    [commitPlaybackTime, totalDurationMs]
  );

  const seekToStroke = useCallback(
    (index: number) => {
      if (strokeCount === 0) return;
      const clamped = Math.min(strokeCount - 1, Math.max(0, Math.floor(index)));
      commitPlaybackTime(clamped * strokeDurationMs, true);
    },
    [commitPlaybackTime, strokeCount, strokeDurationMs]
  );

  const reset = useCallback(() => {
    setIsPlaying(false);
    commitPlaybackTime(0, true);
  }, [commitPlaybackTime]);

  const value = useMemo<ArcPlaybackContextValue>(
    () => ({
      isPlaying,
      playbackSpeed,
      elapsedMs,
      elapsedMsRef,
      totalDurationMs,
      strokeDurationMs,
      strokeCount,
      currentStrokeIndex,
      strokeProgress,
      totalProgress,
      togglePlay,
      setPlaying,
      setPlaybackSpeed,
      seekToProgress,
      seekToStroke,
      reset,
    }),
    [
      currentStrokeIndex,
      elapsedMs,
      elapsedMsRef,
      isPlaying,
      playbackSpeed,
      seekToProgress,
      seekToStroke,
      setPlaying,
      strokeCount,
      strokeDurationMs,
      strokeProgress,
      togglePlay,
      totalDurationMs,
      totalProgress,
      reset,
    ]
  );

  return <ArcPlaybackContext.Provider value={value}>{children}</ArcPlaybackContext.Provider>;
};

export const useArcPlayback = (): ArcPlaybackContextValue => {
  const context = useContext(ArcPlaybackContext);
  if (!context) {
    throw new Error('useArcPlayback must be used within an ArcPlaybackProvider');
  }
  return context;
};
